import { showNotification } from "@mantine/notifications"
import * as Sentry from "@sentry/react"
import _ from "lodash"
import { useCallback, useMemo } from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

import { AxesResponses } from "@costory/types/endpoints/axes"
import { SavedViewsResponses } from "@costory/types/endpoints/savedViews"
import { Filters, FiltersValidator } from "@costory/types/filters"
import { DatePreset } from "@costory/types/prisma-client"

import dayjs from "@costory/shared/dayjs"
import {
  computePresetDates,
  createSafeParsedFilters,
} from "@costory/shared/utils/filters"

import { orderComponentTypes } from "@costory/front/utils/dashboards"

export type DatasourceType =
  | "Datasources"
  | "Virtual Dimensions"
  | "Invoice"
  | "Native labels"
  | "Engineered labels"

// TODO: test it all
export const orderDatasources = (value: DatasourceType) => {
  switch (value) {
    case "Datasources": {
      return 1
    }
    case "Invoice": {
      return 3
    }
    case "Virtual Dimensions": {
      return 2
    }
    case "Native labels": {
      return 4
    }
    case "Engineered labels": {
      return 5
    }
    default: {
      return 6
    }
  }
}

export const formatDatasources = (axes: AxesResponses.Axis[]) => {
  return _.sortBy(
    _.map(
      _.groupBy(axes, (axis) => axis.groupTitle),
      (groupAxes, group) => ({
        group,
        items: groupAxes.map((axis) => ({
          value: axis.name,
          label: axis.label,
        })),
      }),
    ),
    (option) => orderDatasources(option.group as DatasourceType),
  )
}

export const formatSavedViews = (
  savedViews: SavedViewsResponses.SavedView[],
) => {
  return _(savedViews)
    .groupBy("category")
    .map((v, k) => ({
      group: k,
      items: v.map((sv) => ({
        value: sv.id,
        label: sv.name,
      })),
    }))
    .sortBy((g) => orderComponentTypes(g.group))
    .value()
}

export const DEFAULT_QUERY_BUILDER_FILTER = { combinator: "and", rules: [] }
export const DEFAULT_QUERY_BUILDER_FILTER_RECCURING = {
  combinator: "and",
  rules: [
    { field: "cos_charge_frequency", operator: "notIn", value: ["One-Time"] },
  ],
}

export const getParsedSearchParams = (
  searchParamsObj: Record<string, string>,
) => {
  try {
    const {
      datePreset,
      from,
      to,
      previousFrom,
      previousTo,
      whereClause,
      aggBy,
      bookmarkedEvents,
      eventsFilter,
      ...rest
    } = searchParamsObj

    const isPreset = !!datePreset
    const computedDates = isPreset
      ? computePresetDates(datePreset as DatePreset)
      : {
          from,
          to,
          previousFrom,
          previousTo,
        }

    return {
      ...rest,
      datePreset: isPreset ? datePreset : null,
      ...computedDates,
      aggBy,
      whereClause: JSON.parse(whereClause),
      bookmarkedEvents: bookmarkedEvents
        ? bookmarkedEvents.split(",")
        : undefined,
      eventsFilter: eventsFilter ? JSON.parse(eventsFilter) : undefined,
    }
  } catch (err) {
    console.error("Failed to parse search params", err)
    Sentry.captureException(
      new Error("Invalid Search filters: " + JSON.stringify(searchParamsObj)),
    )
    return undefined
  }
}

export const useFiltersSearchParamsBinding = (
  onValidateFilters: FiltersValidator["safeParse"],
) => {
  const [searchParams] = useSearchParams()

  const urlFilters = useMemo<Filters | undefined>(() => {
    if (!searchParams.get("metricId")) return undefined

    const searchParamsObj = Object.fromEntries(searchParams)

    // TODO: Need to add a proper fix for the dates between timezone issue
    // FixMe: https://github.com/costory-io/costory-app/issues/508
    const parsedSearchParams = getParsedSearchParams(searchParamsObj)
    const validatedSearchParams = onValidateFilters(parsedSearchParams)
    if (!validatedSearchParams.success) {
      Sentry.captureException(
        new Error(
          "Invalid URL filters: " +
            JSON.stringify(validatedSearchParams.error.issues) +
            " " +
            JSON.stringify(parsedSearchParams) +
            JSON.stringify(searchParamsObj),
        ),
      )
      if (parsedSearchParams) {
        showNotification({
          color: "red",
          title: "Invalid URL parameters",
          message:
            "The filters set in the URL are invalid. Please check your browser console for more information. Default filter values will be used.",
        })
        console.log(
          "ERROR validating URL filters",
          validatedSearchParams.error.issues,
        )
      }
      return undefined
    }

    return validatedSearchParams.data
  }, [searchParams, onValidateFilters])
  const location = useLocation()
  const navigate = useNavigate()
  const memoizedSafeParsedFilters = useMemo(
    () => (filters: Filters | null) => createSafeParsedFilters(filters),
    [],
  )
  const setURLFilters = useCallback(
    (filters: Filters | null) => {
      const safeParsedFilters = memoizedSafeParsedFilters(filters)
      const newSearch = new URLSearchParams(safeParsedFilters).toString()
      const nextSearch = newSearch ? `?${newSearch}` : ""
      if (nextSearch !== location.search) {
        if (!nextSearch) {
          navigate(location.pathname)
          return
        }
        navigate(nextSearch)
      }
    },
    [navigate, location.search, location.pathname, memoizedSafeParsedFilters],
  )

  return { urlFilters, setURLFilters }
}

export const toReadableDate = (
  date: Date | string | null | undefined,
): string => {
  if (!date) {
    return ""
  }
  return dayjs(date).format("YYYY MMM DD")
}
